import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { invalidateGetNewsletterTemplatesByKeyword } from '../../../../queries';
import {
	invalidateTemplateKeywords,
	useTemplateKeywords,
} from '../../../../queries/Templates/useEmailTemplateKeywords';
import { invalidateAllTemplateSystemQuery } from '../../../../queries/Templates/useGetTemplatesByIndustry';
import { useUpdateEmailTemplateKeywords } from '../../../../queries/Templates/useUpdateEmailTemplateKeywords';
import { Button } from '../../../components-v2/Button';
import { Pill } from '../../../components-v2/Pill/Pill';
import { PlusIcon } from '../../svgs/icons/PlusIcon';
import { FolderIcon } from '../../svgs/icons/FolderIcon';
import { extractFolderName } from '../../../containers/Campaigns/folderUtils';
import { bs } from '../../../styles/styles';
import { KeywordCombobox } from './KeywordCombobox';
import { styleSheet } from './styles';

export interface IProps {
	keywords: string[];
	templateId: string;
	industry: Api.Industry;
	invalidateTemplates: () => void;
	readOnly?: boolean;
}

export const TemplateKeywords: React.FC<IProps> = ({
	industry,
	keywords,
	templateId,
	invalidateTemplates,
	readOnly,
}) => {
	const userSession = useUserSession();
	const [addingKeyword, setAddingKeyword] = React.useState(false);

	const keywordsQuery = useTemplateKeywords({ industry });

	const keywordsMutation = useUpdateEmailTemplateKeywords({
		impersonationContext: userSession.impersonationContext,
		onSuccess: () => {
			invalidateTemplates();
			invalidateTemplateKeywords({ industry });
		},
	});

	const cardKeywords = keywords ?? [];

	const handleSelectItem = (selected: string) => {
		if (selected && !cardKeywords.find(keyword => keyword === selected)) {
			const updated = [...cardKeywords.filter(keyword => !!keyword), selected];
			keywordsMutation.mutate({ keywords: updated, templateId });
		}
	};

	const handleRemove = (selected: string) => {
		const updated = cardKeywords.filter(k => k !== selected);
		keywordsMutation.mutate({ keywords: updated, templateId });
		invalidateGetNewsletterTemplatesByKeyword({
			impersonationContext: userSession.impersonationContext,
			industry,
			templateType: Api.TemplateType.HtmlNewsletter,
		});
		invalidateAllTemplateSystemQuery();
	};

	const handleKeywordSelect = (keyword: string) => {
		handleSelectItem(keyword);
		setAddingKeyword(false);
	};

	const isDisabled = readOnly || keywordsMutation.isPending || keywordsQuery.isPending;

	return (
		<div className={css(styleSheet.keywordsContainer)}>
			{cardKeywords.length > 0 && (
				<div className={css(bs.flex, bs.flexWrap, bs.gap2, bs.mb2)}>
					{cardKeywords.map((keyword, i) => {
						const folderName = extractFolderName(keyword);
						return (
							<Pill
								key={`${i}-${keyword}`}
								variant='keyword'
								label={folderName ?? keyword}
								value={keyword}
								icon={folderName ? <FolderIcon fillColor='currentColor' /> : undefined}
								disabled={keywordsMutation.isPending}
								onRemove={readOnly ? undefined : handleRemove}
							/>
						);
					})}
				</div>
			)}
			{!readOnly &&
				(addingKeyword ? (
					<KeywordCombobox
						keywords={keywordsQuery.data ?? []}
						selectedKeywords={cardKeywords}
						disabled={isDisabled}
						onSelect={handleKeywordSelect}
						onClose={() => setAddingKeyword(false)}
					/>
				) : (
					<Button variant='link' disabled={isDisabled} onClick={() => setAddingKeyword(true)}>
						<PlusIcon />
						Add Keyword
					</Button>
				))}
		</div>
	);
};
